import LinkIcon from "@mui/icons-material/Link";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";

export interface FactCheckFinding {
  id: number;
  section_heading: string;
  category:
    | "factual_error"
    | "harmful_to_client"
    | "link_issue"
    | "outdated_info"
    | "unverifiable_claim"
    | string;
  severity: "critical" | "warning" | "suggestion" | string;
  original_text: string;
  replacement_text: string | null;
  reasoning: string;
  source: string;
  action: "replace" | "remove" | "flag_for_review" | string;
  applied?: boolean;
  locator?: {
    start_char?: number | null;
    end_char?: number | null;
    context_before?: string | null;
    context_after?: string | null;
    match_strategy?: string;
  };
}

type FilterType = "all" | "critical" | "warning" | "suggestion";

interface FactCheckSidebarContentProps {
  findings: FactCheckFinding[];
  onSelectFinding?: (finding: FactCheckFinding) => void;
  activeFindingId?: number | null;
  onApplyAction?: (finding: FactCheckFinding) => void;
}

const severityColor = (severity: string) => {
  if (severity === "critical") return { bg: "#FEE2E2", text: "#B91C1C" };
  if (severity === "warning") return { bg: "#FEF3C7", text: "#B45309" };
  return { bg: "#E0E7FF", text: "#3730A3" };
};

const categoryLabel = (category: string) =>
  category
    .split("_")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");

const FactCheckSidebarContent: React.FC<FactCheckSidebarContentProps> = ({
  findings = [],
  onSelectFinding,
  activeFindingId,
  onApplyAction,
}) => {
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Scroll to the active card whenever the active finding changes (e.g. user clicked underlined text)
  useEffect(() => {
    if (activeFindingId == null) return;
    const el = cardRefs.current[String(activeFindingId)];
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeFindingId]);

  const criticalCount = findings.filter((f) => f.severity === "critical").length;
  const warningCount = findings.filter((f) => f.severity === "warning").length;
  const suggestionCount = findings.filter((f) => f.severity === "suggestion").length;

  const filteredFindings = useMemo(() => {
    if (activeFilter === "all") return findings;
    return findings.filter((f) => f.severity === activeFilter);
  }, [activeFilter, findings]);

  const handleCopyText = async (
    event: React.MouseEvent,
    text: string,
    key: string
  ) => {
    event.stopPropagation();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      window.setTimeout(() => {
        setCopiedKey((prev) => (prev === key ? null : prev));
      }, 1500);
    } catch (error) {
      console.error("Failed to copy text", error);
    }
  };

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "20px", fontFamily: "Inter", color: "#121828", mb: 0.5 }}>
          Fact Check Findings
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "#6B7280", fontFamily: "Inter" }}>
          Review issues and apply corrections before publishing.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <Card
          onClick={() => setActiveFilter("critical")}
          sx={{ flex: 1, p: 1.5, cursor: "pointer", border: activeFilter === "critical" ? "2px solid #B91C1C" : "1px solid #E5E7EB", boxShadow: "none" }}
        >
          <Typography sx={{ fontSize: "12px", color: "#B91C1C", fontWeight: 600 }}>Critical</Typography>
          <Typography sx={{ fontSize: "24px", fontWeight: 700 }}>{criticalCount}</Typography>
        </Card>
        <Card
          onClick={() => setActiveFilter("warning")}
          sx={{ flex: 1, p: 1.5, cursor: "pointer", border: activeFilter === "warning" ? "2px solid #B45309" : "1px solid #E5E7EB", boxShadow: "none" }}
        >
          <Typography sx={{ fontSize: "12px", color: "#B45309", fontWeight: 600 }}>Warning</Typography>
          <Typography sx={{ fontSize: "24px", fontWeight: 700 }}>{warningCount}</Typography>
        </Card>
        <Card
          onClick={() => setActiveFilter("suggestion")}
          sx={{ flex: 1, p: 1.5, cursor: "pointer", border: activeFilter === "suggestion" ? "2px solid #3730A3" : "1px solid #E5E7EB", boxShadow: "none" }}
        >
          <Typography sx={{ fontSize: "12px", color: "#3730A3", fontWeight: 600 }}>Suggestion</Typography>
          <Typography sx={{ fontSize: "24px", fontWeight: 700 }}>{suggestionCount}</Typography>
        </Card>
      </Box>

      {activeFilter !== "all" && (
        <Box sx={{ mb: 2, textAlign: "center" }}>
          <Button onClick={() => setActiveFilter("all")} size="small" sx={{ textTransform: "none" }}>
            Show all findings
          </Button>
        </Box>
      )}

      {filteredFindings.length === 0 ? (
        <Card sx={{ p: 2, border: "1px solid #E5E7EB", boxShadow: "none" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TaskAltIcon sx={{ color: "#16A34A" }} />
            <Typography sx={{ fontSize: "14px", color: "#111827" }}>No findings for this filter.</Typography>
          </Box>
        </Card>
      ) : (
        filteredFindings.map((finding) => {
          const color = severityColor(finding.severity);
          const normalizedAction = String(finding.action || "").trim().toLowerCase();
          const isReplaceAction = normalizedAction === "replace";
          const isRemoveAction = normalizedAction === "remove";
          const isApplied = Boolean(finding.applied);
          return (
            <Card
              key={`${finding.id}-${finding.original_text.slice(0, 24)}`}
              ref={(el) => { cardRefs.current[String(finding.id)] = el as HTMLDivElement | null; }}
              onClick={(event) => {
                const target = event.target as HTMLElement;
                if (target.closest('[data-disable-card-select="true"]')) return;
                onSelectFinding?.(finding);
              }}
              sx={{
                p: 2,
                mb: 2,
                border: "1px solid #E5E7EB",
                boxShadow: "none",
                cursor: onSelectFinding ? "pointer" : "default",
                "&:hover": onSelectFinding
                  ? { borderColor: "#6366F1", boxShadow: "0 0 0 1px #6366F1 inset" }
                  : {},
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Chip label={finding.severity.toUpperCase()} size="small" sx={{ backgroundColor: color.bg, color: color.text, fontWeight: 700 }} />
                <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>{categoryLabel(finding.category)}</Typography>
              </Box>

              <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>Section: {finding.section_heading || "Introduction"}</Typography>
              <Box
                data-disable-card-select="true"
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
              >
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>Original</Typography>
                <Tooltip title="Copy original text">
                  <IconButton
                    size="small"
                    onClick={(event) =>
                      handleCopyText(event, finding.original_text, `original-${finding.id}`)
                    }
                    sx={{ p: "2px" }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                {copiedKey === `original-${finding.id}` && (
                  <Typography sx={{ fontSize: "11px", color: "#16A34A", fontWeight: 600 }}>
                    Copied
                  </Typography>
                )}
              </Box>
              <Typography data-disable-card-select="true" sx={{ fontSize: "13px", color: "#374151", mb: 1.5, userSelect: "text" }}>
                {finding.original_text}
              </Typography>

              <Box
                data-disable-card-select="true"
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
              >
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>
                  {isRemoveAction
                    ? "Action: Remove"
                    : normalizedAction === "flag_for_review"
                    ? "Action: Review Needed"
                    : "Suggested Replacement"}
                </Typography>
                {finding.replacement_text && (
                  <Tooltip title="Copy replacement text">
                    <span>
                      <IconButton
                        size="small"
                        onClick={(event) =>
                          handleCopyText(
                            event,
                            finding.replacement_text || "",
                            `replacement-${finding.id}`
                          )
                        }
                        sx={{ p: "2px" }}
                        disabled={!finding.replacement_text}
                      >
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {copiedKey === `replacement-${finding.id}` && (
                  <Typography sx={{ fontSize: "11px", color: "#16A34A", fontWeight: 600 }}>
                    Copied
                  </Typography>
                )}
              </Box>
              <Typography data-disable-card-select="true" sx={{ fontSize: "13px", color: "#374151", mb: 1.5, userSelect: "text" }}>
                {finding.replacement_text || "No replacement text provided."}
              </Typography>

              {(isReplaceAction || isRemoveAction || isApplied) && (
                <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(event) => {
                      event.stopPropagation();
                      onApplyAction?.(finding);
                    }}
                    disabled={
                      isApplied ||
                      !onApplyAction ||
                      (isReplaceAction && !finding.replacement_text?.trim())
                    }
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      minWidth: "unset",
                      px: 1.5,
                      py: 0.5,
                      backgroundColor: isApplied ? "#22C55E" : undefined,
                      "&.Mui-disabled": {
                        backgroundColor: "#22C55E",
                        color: "#FFFFFF",
                        opacity: 1,
                      },
                    }}
                  >
                    {isApplied
                      ? "Changes applied"
                      : isReplaceAction
                      ? "Apply Replace"
                      : "Apply Remove"}
                  </Button>
                </Box>
              )}

              <Box data-disable-card-select="true" sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
                <ReportProblemOutlinedIcon sx={{ fontSize: 16, color: "#6B7280", mt: "2px", flexShrink: 0 }} />
                <Typography sx={{ fontSize: "12px", color: "#4B5563", userSelect: "text", flex: 1 }}>{finding.reasoning}</Typography>
                <Tooltip title="Copy reasoning">
                  <IconButton
                    size="small"
                    onClick={(event) =>
                      handleCopyText(event, finding.reasoning, `reasoning-${finding.id}`)
                    }
                    sx={{ p: "2px", flexShrink: 0 }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                {copiedKey === `reasoning-${finding.id}` && (
                  <Typography sx={{ fontSize: "11px", color: "#16A34A", fontWeight: 600, flexShrink: 0 }}>
                    Copied
                  </Typography>
                )}
              </Box>

              {finding.source && (() => {
                const parts = finding.source
                  .split(/\s*;\s*/)
                  .map((u) => u.trim())
                  .filter((u) => u.length > 0);
                return (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {parts.map((part, idx) => {
                      const isUrl = /^https?:\/\//i.test(part);
                      return (
                        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <LinkIcon sx={{ fontSize: 14, color: "#6366F1", flexShrink: 0 }} />
                          {isUrl ? (
                            <Link href={part} target="_blank" rel="noopener noreferrer" sx={{ fontSize: "12px", wordBreak: "break-all" }}>
                              {part}
                            </Link>
                          ) : (
                            <Typography sx={{ fontSize: "12px", color: "#4B5563" }}>{part}</Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })()}
            </Card>
          );
        })
      )}
    </Box>
  );
};

export default FactCheckSidebarContent;
